<template>
  <v-modal 
    v-model="showModal" 
    modal-title="Öneri Detayları" 
    icon="auto_fix_high" 
    size="lg" 
    z-index="100000"
    :noPadding="true"
  >
    <div v-if="!suggestion" class="p-6 text-center">
      <span class="material-symbols-outlined text-4xl text-gray-400 mb-2">error</span>
      <p class="text-lg font-medium text-gray-900">Öneri Bulunamadı</p>
    </div>

    <div v-else>
      <div class="flex flex-col md:flex-row">
        <!-- Sol Sütun: Öneri Detayları -->
        <div class="w-full md:w-1/2 p-4 md:p-6 bg-gray-50" v-show="activeTab === 'info' || isDesktop">
          <!-- Oluşturma Bilgileri -->
          <div class="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <div class="p-4 bg-gray-50 border-b border-gray-200 flex items-center">
              <span class="material-symbols-outlined text-gray-600 mr-2">history</span>
              <h3 class="font-medium text-gray-800">Oluşturma Bilgileri</h3>
            </div>
            
            <div class="p-5">
              <div class="flex items-center mb-3">
                <div class="flex-shrink-0 w-8 h-8 rounded-full bg-primary bg-opacity-10 flex items-center justify-center mr-3">
                  <span class="material-symbols-outlined text-primary text-lg">person</span>
                </div>
                <div>
                  <div class="font-medium">{{ suggestion.userName || 'Anonim Kullanıcı' }}</div>
                  <div class="text-xs text-gray-500">{{ suggestion.userEmail || 'Email bilgisi yok' }}</div>
                </div>
              </div>

              <div class="flex items-center">
                <div class="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                  <span class="material-symbols-outlined text-blue-600 text-lg">calendar_today</span>
                </div>
                <div>
                  <div class="font-medium">Oluşturulma Tarihi</div>
                  <div class="text-xs text-gray-500">{{ new Date(suggestion.createdAt).toLocaleString('tr-TR') }}</div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Üst Bilgi Kartı -->
          <div class="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <div class="p-4 bg-blue-50 border-b border-gray-200 flex items-center justify-between">
              <h3 class="font-medium text-gray-800 flex items-center">
                <span class="material-symbols-outlined text-blue-600 mr-2">description</span>
                Öneri Detayı
              </h3>
              <span class="text-xs font-medium px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                {{ getContentTypeLabel(suggestion.contentType) }}
              </span>
            </div>
            
            <div class="p-5">
              <div class="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm whitespace-pre-wrap max-h-40 overflow-y-auto">
                {{ suggestion.suggestion }}
              </div>
              
              <div class="mt-4 flex items-center">
                <div class="flex-shrink-0">
                  <span class="text-sm font-medium mr-2">Öncelik:</span>
                </div>
                <div class="flex items-center gap-2">
                  <div class="h-2 w-20 bg-gray-200 rounded-full overflow-hidden">
                    <div class="h-full" 
                      :class="getPriorityBarClass(suggestion.priority)" 
                      :style="{ width: `${suggestion.priority * 20}%` }">
                    </div>
                  </div>
                  <span class="text-xs font-medium px-2 py-1 rounded-full" 
                    :class="{
                      'bg-green-100 text-green-800': suggestion.priority <= 2,
                      'bg-yellow-100 text-yellow-800': suggestion.priority === 3,
                      'bg-red-100 text-red-800': suggestion.priority >= 4
                    }">
                    {{ suggestion.priority }} - {{ getPriorityLabel(suggestion.priority) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Sağ Sütun: İçerik Detayları -->
        <div class="w-full md:w-1/2 p-4 md:p-6 border-l-0 md:border-l border-gray-200" v-show="activeTab === 'content' || isDesktop">
          
          <!-- Görsel İçeriği Görünümü -->
          <div v-if="suggestion.contentType === 'IMAGE'" class="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <div class="p-4 bg-purple-50 border-b border-gray-200 flex items-center">
              <span class="material-symbols-outlined text-purple-600 mr-2">image</span>
              <h3 class="font-medium text-gray-800">Önerilen Görsel</h3>
            </div>
            
            <div class="p-5">
              <div v-if="suggestion.currentImageUrl" class="flex justify-center bg-gray-50 border border-gray-200 rounded-lg overflow-hidden p-2 mb-3">
                <img :src="suggestion.currentImageUrl" alt="Önerilen Görsel" class="max-h-60 max-w-full object-contain">
              </div>
              <div v-else class="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                <span class="material-symbols-outlined text-gray-400 text-3xl mb-2">image_not_supported</span>
                <p class="text-gray-500 text-sm">Görsel mevcut değil</p>
              </div>

              <div v-if="suggestion.currentImageUrl" class="mt-3 text-sm">
                <div class="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                  <span class="text-gray-600 truncate block mr-2 flex-grow">{{ suggestion.currentImageUrl }}</span>
                  <a :href="suggestion.currentImageUrl" target="_blank" class="text-primary hover:text-primary-dark flex-shrink-0">
                    <span class="material-symbols-outlined">open_in_new</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <!-- Video İçeriği Görünümü -->
          <div v-if="suggestion.contentType === 'VIDEO'" class="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <div class="p-4 bg-red-50 border-b border-gray-200 flex items-center">
              <span class="material-symbols-outlined text-red-600 mr-2">videocam</span>
              <h3 class="font-medium text-gray-800">Önerilen Video</h3>
            </div>
            
            <div class="p-5">
              <div v-if="suggestion.currentVideoUrl" class="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
                <div class="flex items-center justify-between">
                  <div class="flex items-center flex-grow truncate">
                    <span class="material-symbols-outlined text-red-500 mr-2">play_circle</span>
                    <span class="text-gray-700 text-sm truncate">{{ suggestion.currentVideoUrl }}</span>
                  </div>
                  <a :href="suggestion.currentVideoUrl" target="_blank" class="text-primary hover:text-primary-dark flex-shrink-0 ml-2">
                    <span class="material-symbols-outlined">open_in_new</span>
                  </a>
                </div>
              </div>
              <div v-else class="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                <span class="material-symbols-outlined text-gray-400 text-3xl mb-2">videocam_off</span>
                <p class="text-gray-500 text-sm">Video linki mevcut değil</p>
              </div>
            </div>
          </div>

          <!-- Metin İçeriği Görünümü -->
          <div v-if="suggestion.contentType === 'TEXT'" class="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <div class="p-4 bg-blue-50 border-b border-gray-200 flex items-center justify-between">
              <div class="flex items-center">
                <span class="material-symbols-outlined text-blue-600 mr-2">text_format</span>
                <h3 class="font-medium text-gray-800">Önerilen Metin</h3>
              </div>
              <span v-if="suggestion.currentText" class="text-xs text-gray-500">
                {{ suggestion.currentText.length.toLocaleString('tr-TR') }} karakter
              </span>
            </div>
            
            <div class="p-5">
              <div v-if="suggestion.currentText" class="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-96 overflow-y-auto">
                <div class="prose max-w-none text-sm" v-html="suggestion.currentText"></div>
              </div>
              <div v-else class="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                <span class="material-symbols-outlined text-gray-400 text-3xl mb-2">article</span>
                <p class="text-gray-500 text-sm">Metin içeriği mevcut değil</p>
              </div>
            </div>
          </div>
          
          <!-- İçeriğe Git Butonu - Custom Button -->
          <div v-if="suggestion.contentId" class="bg-white rounded-lg shadow-sm overflow-hidden p-5 mb-6 border border-gray-200">
            <div class="flex flex-col items-center gap-3">
              <div class="text-center bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p class="text-sm text-gray-600 mb-3">
                  İçeriğin tam versiyonunu görmek için içerik sayfasına gidebilirsiniz
                </p>
                <a 
                  @click="goToContentView" 
                  class="inline-flex items-center text-primary font-medium hover:text-primary-dark cursor-pointer"
                >
                  <span class="mr-1">İçeriği detaylı incele</span>
                  <span class="material-symbols-outlined">open_in_new</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer - Relative -->
      <div class="border-t border-gray-200 bg-gray-50 p-3 flex justify-between items-center relative mt-auto z-10 shadow-md">
        <div class="text-xs text-gray-500">
          <span class="material-symbols-outlined text-amber-500 mr-1 text-sm align-text-bottom">update</span>
          Son güncelleme: {{ suggestion.updatedAt ? new Date(suggestion.updatedAt).toLocaleString('tr-TR') : 'Henüz güncellenmedi' }}
        </div>

        <div class="flex gap-2">
          <v-button 
            @click="$emit('edit')" 
            button-text="Düzenle" 
            color="primary" 
            type="button" 
            icon="edit" 
            size="sm"
          />
          <v-button 
            @click="closeModal" 
            button-text="Kapat" 
            color="secondary" 
            type="button" 
            icon="close" 
            size="sm"
          />
        </div>
      </div>
    </div>
  </v-modal>
</template>

<script setup>
import { computed, watch, reactive, ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useContentStore } from '@/stores/content'
import VModal from '@/components/modals/VModal.vue'
import VButton from '@/components/base/buttons/VButton.vue'
import { useSuggestionView } from '../../composables'
import {
  getContentTypeLabel,
  getContentCategoryLabel,
  getPriorityBarClass,
  getPriorityLabel,
} from '../../constants/suggestions'

const router = useRouter()
const contentStore = useContentStore()

// Mobil tab yönetimi için
const activeTab = ref('info') // 'info' veya 'content'

// Desktop için responsive kontrol
const isDesktop = ref(false)
const checkScreenSize = () => {
  isDesktop.value = window.innerWidth >= 768 // md breakpoint
}

// Component yüklenirken ve pencere boyutu değiştiğinde screen size kontrolü
onMounted(() => {
  checkScreenSize()
  window.addEventListener('resize', checkScreenSize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkScreenSize)
})

// Suggestion görüntüleme composable'ını kullan
const {
  currentSuggestion,
  isLoading, 
  error, 
  loadSuggestion, 
  formatSuggestion 
} = useSuggestionView()

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  suggestion: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['update:modelValue', 'edit'])

// Props değişikliklerini takip ederek suggestion yükleme
watch(() => props.suggestion, (newSuggestion) => {
  if (newSuggestion && newSuggestion.id) {
    // Props'tan gelen suggestion verisi state'e aktarılır
    currentSuggestion.value = newSuggestion
  }
}, { immediate: true })

const showModal = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const closeModal = () => {
  showModal.value = false
}

const goToContentView = () => {
  if (props.suggestion && props.suggestion.contentId) {
    // Modal'ı kapat
    closeModal()

    // İçerik sayfasına yönlendir
    router.push({
      name: 'contentModerator',
      query: { tab: 'contents' },
    }).then(() => {
      // İçerik detayını göster
      contentStore.showContentDetail(router, props.suggestion.contentId)
    })
  }
}
</script>
